<template>
  <v-card 
    v-if="isVisible" 
    :style="sectionStyle" 
    class="mb-6 section-card" 
    elevation="0"
  >
    <v-card-title class="pb-2">
      <h3 class="text-h6 font-weight-semibold d-flex align-center mb-0" style="color: #1f2937;">
        <v-icon class="mr-2" style="color: #6b7280;">mdi-clipboard-list</v-icon>
        Panel Intelligence
      </h3>
    </v-card-title>
    <div class="px-4 pb-2">
      <p class="subtitle text-body mb-0" style="color: #6b7280;">Insights into panel memberships, competitor activity, and new panel opportunities</p>
    </div>

    <v-tabs v-model="currentTab" slider-color="#ff1463" style="border-bottom: 1px solid #e5e7eb;">
      <v-tab style="text-transform: none; font-size: 14px; font-weight: 500;">
        Competitors Joined
        <v-chip x-small style="margin-left: 8px; height: 20px; background-color: #f3f4f6; color: #6b7280;">
          {{ competitorsJoinedCount }}
        </v-chip>
      </v-tab>
      <v-tab style="text-transform: none; font-size: 14px; font-weight: 500;">
        Suggested Panels
        <v-chip x-small style="margin-left: 8px; height: 20px; background-color: #f3f4f6; color: #6b7280;">
          {{ suggestedPanelsCount }}
        </v-chip>
      </v-tab>
      <v-tab style="text-transform: none; font-size: 14px; font-weight: 500;">
        Newly Created
        <v-chip x-small style="margin-left: 8px; height: 20px; background-color: #f3f4f6; color: #6b7280;">
          {{ newlyCreatedCount }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <v-card-text class="pa-0">
      <!-- Competitors Recently Joined -->
      <div v-if="currentTab === 0">
        <div v-if="paginatedPanels.length === 0" class="text-center py-8 grey--text">
          No competitors recently joined panels
        </div>
        <div v-else>
          <div 
            v-for="(panel, index) in paginatedPanels" 
            :key="panel.panel_id || index"
            class="pa-6"
            :style="'border-bottom: 1px solid #e5e7eb; background-color: #fef2f2; border-left: 3px solid #f87171;' + (index > 0 ? ' border-top: 1px solid #e5e7eb;' : '')"
          >
            <div class="d-flex justify-space-between align-start">
              <div class="flex-grow-1">
                <router-link 
                  v-if="panel.son_id && panel.gov_type" 
                  :to="`/panel?son_id=${panel.son_id}&government=${panel.gov_type}`" 
                  target="_blank" 
                  class="text-h6 font-weight-medium text-decoration-none d-flex align-center mb-2" 
                  style="color: #1f2937;"
                >
                  {{ panel.title }}
                  <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
                </router-link>
                <a 
                  v-else-if="panel.panel_url" 
                  :href="panel.panel_url" 
                  target="_blank" 
                  class="text-h6 font-weight-medium text-decoration-none d-flex align-center mb-2" 
                  style="color: #1f2937;"
                >
                  {{ panel.title }}
                  <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
                </a>
                <div v-else class="text-h6 font-weight-medium mb-2" style="color: #1f2937;">
                  {{ panel.title }}
                </div>
                <div class="d-flex align-center gap-4 text-body-2 grey--text">
                  <span class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-account</v-icon>
                    <router-link 
                      v-if="panel.competitor_supplier_id" 
                      :to="`/supplier/${panel.competitor_supplier_id}`" 
                      class="text-decoration-none" 
                      style="color: inherit;"
                      target="_blank"
                    >
                      {{ panel.competitor_supplier_name }}
                    </router-link>
                    <span v-else>{{ panel.competitor_supplier_name }}</span>
                  </span>
                  <span class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-map-marker</v-icon>
                    {{ panel.agency }}
                  </span>
                  <span v-if="panel.deed_start" class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-calendar</v-icon>
                    Joined: {{ formatDate(panel.deed_start) }}
                  </span>
                  <v-chip v-if="panel.primary_category_name" label x-small color="secondary" text-color="white">
                    {{ panel.primary_category_name }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Suggested Panels -->
      <div v-if="currentTab === 1">
        <div v-if="paginatedPanels.length === 0" class="text-center py-8 grey--text">
          No suggested panels found
        </div>
        <div v-else>
          <div 
            v-for="(panel, index) in paginatedPanels" 
            :key="panel.panel_id || index"
            class="pa-6"
            :style="'border-bottom: 1px solid #e5e7eb; background-color: #fef3c7; border-left: 3px solid #f59e0b;' + (index > 0 ? ' border-top: 1px solid #e5e7eb;' : '')"
          >
            <div class="d-flex justify-space-between align-start">
              <div class="flex-grow-1">
                <router-link 
                  v-if="panel.son_id && panel.gov_type" 
                  :to="`/panel?son_id=${panel.son_id}&government=${panel.gov_type}`" 
                  target="_blank" 
                  class="text-h6 font-weight-medium text-decoration-none d-flex align-center mb-2" 
                  style="color: #1f2937;"
                >
                  {{ panel.title }}
                  <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
                </router-link>
                <a 
                  v-else-if="panel.panel_url" 
                  :href="panel.panel_url" 
                  target="_blank" 
                  class="text-h6 font-weight-medium text-decoration-none d-flex align-center mb-2" 
                  style="color: #1f2937;"
                >
                  {{ panel.title }}
                  <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
                </a>
                <div v-else class="text-h6 font-weight-medium mb-2" style="color: #1f2937;">
                  {{ panel.title }}
                </div>
                <div class="d-flex align-center gap-4 text-body-2 grey--text">
                  <span class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-map-marker</v-icon>
                    {{ panel.agency }}
                  </span>
                  <span class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-account-group</v-icon>
                    {{ panel.competitor_count }} competitor{{ panel.competitor_count !== 1 ? 's' : '' }} on panel
                  </span>
                  <v-chip v-if="panel.primary_category_name" label x-small color="secondary" text-color="white">
                    {{ panel.primary_category_name }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Newly Created Panels -->
      <div v-if="currentTab === 2">
        <div v-if="paginatedPanels.length === 0" class="text-center py-8 grey--text">
          No newly created panels found
        </div>
        <div v-else>
          <div 
            v-for="(panel, index) in paginatedPanels" 
            :key="panel.panel_id || index"
            class="pa-6"
            :style="'border-bottom: 1px solid #e5e7eb; background-color: #dbeafe; border-left: 3px solid #3b82f6;' + (index > 0 ? ' border-top: 1px solid #e5e7eb;' : '')"
          >
            <div class="d-flex justify-space-between align-start">
              <div class="flex-grow-1">
                <router-link 
                  v-if="panel.son_id && panel.gov_type" 
                  :to="`/panel?son_id=${panel.son_id}&government=${panel.gov_type}`" 
                  target="_blank" 
                  class="text-h6 font-weight-medium text-decoration-none d-flex align-center mb-2" 
                  style="color: #1f2937;"
                >
                  {{ panel.title }}
                  <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
                </router-link>
                <a 
                  v-else-if="panel.panel_url" 
                  :href="panel.panel_url" 
                  target="_blank" 
                  class="text-h6 font-weight-medium text-decoration-none d-flex align-center mb-2" 
                  style="color: #1f2937;"
                >
                  {{ panel.title }}
                  <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
                </a>
                <div v-else class="text-h6 font-weight-medium mb-2" style="color: #1f2937;">
                  {{ panel.title }}
                </div>
                <div class="d-flex align-center gap-4 text-body-2 grey--text">
                  <span class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-map-marker</v-icon>
                    {{ panel.agency }}
                  </span>
                  <span v-if="panel.publish_date" class="d-flex align-center">
                    <v-icon small class="mr-1">mdi-calendar</v-icon>
                    Created: {{ formatDate(panel.publish_date) }}
                  </span>
                  <v-chip v-if="panel.primary_category_name" label x-small color="secondary" text-color="white">
                    {{ panel.primary_category_name }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <v-card-actions 
        v-if="totalPages > 1" 
        class="pa-4" 
        style="border-top: 1px solid #e5e7eb; background-color: #f9fafb;"
      >
        <div class="d-flex justify-space-between align-center" style="width: 100%;">
          <div class="text-body" style="color: #6b7280;">
            Showing {{ paginationStart }} - {{ paginationEnd }} of {{ currentDataLength }}
          </div>
          <div class="d-flex gap-2">
            <v-btn
              icon
              x-small
              :disabled="currentPage === 1"
              @click="goToPreviousPage"
              style="color: #6b7280;"
            >
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              icon
              x-small
              :disabled="currentPage >= totalPages"
              @click="goToNextPage"
              style="color: #6b7280;"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card-actions>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'PanelIntelligenceSection',
  
  props: {
    panelIntelligence: {
      type: Object,
      default: () => ({
        competitors_recently_joined: [],
        suggested_panels: [],
        newly_created_panels: []
      })
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    sectionStyle: {
      type: String,
      default: ''
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      currentTab: 0, // 0: recently joined, 1: suggested, 2: newly created
      currentPage: 1
    };
  },

  computed: {
    competitorsJoinedCount() {
      return (this.panelIntelligence.competitors_recently_joined || []).length;
    },

    suggestedPanelsCount() {
      return (this.panelIntelligence.suggested_panels || []).length;
    },

    newlyCreatedCount() {
      return (this.panelIntelligence.newly_created_panels || []).length;
    },

    currentData() {
      if (this.currentTab === 0) {
        return this.panelIntelligence.competitors_recently_joined || [];
      } else if (this.currentTab === 1) {
        return this.panelIntelligence.suggested_panels || [];
      } else {
        return this.panelIntelligence.newly_created_panels || [];
      }
    },

    paginatedPanels() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.currentData.slice(start, end);
    },

    totalPages() {
      return this.currentData.length > 0 ? Math.ceil(this.currentData.length / this.itemsPerPage) : 0;
    },

    currentDataLength() {
      return this.currentData.length;
    },

    paginationStart() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.currentDataLength);
    }
  },

  watch: {
    currentTab() {
      // Reset to first page when tab changes
      this.currentPage = 1;
    },
    panelIntelligence: {
      deep: true,
      handler() {
        // Reset to first page when data changes
        this.currentPage = 1;
      }
    }
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) {
        return String(dateStr);
      }
    },

    goToPreviousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    goToNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    }
  }
};
</script>

<style scoped>
.section-card {
  border: 1px solid #e5e7eb;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}
</style>
